// Report It submission flow (v7 §6.1, MV-1077). Ties the client-snapshot
// capture to the API client: capture → assemble → submit. Dependency-injected
// (capture / submit / element accessors / entity provider) so the whole flow is
// unit-tested; the React hook that wires the live GameCast context is then a
// thin wrapper that supplies the real dependencies.

import {
  ReportItClientSnapshot,
} from './snapshotTypes';
import { CaptureReportItSnapshotOptions } from './captureSnapshot';
import {
  SubmitReportInput,
  SubmitReportResponse,
  ReportCorrelationEntities,
  ReportAttachmentFile,
  ReportIncidentType,
  ReportSymptomHint,
  ReportSeverityHint,
  collectBrowserReportContext,
  buildAttachmentDeclarations,
  uploadReportAttachments,
} from './reportItApi';

/** The user- + session-supplied fields of a report. The flow fills in the
 *  captured snapshot, browser context, and correlation entities. */
export interface ReportDraft {
  environment: string;
  eventId?: string;
  /** Idempotency inputs (v7 §10.3) — generated once when the dialog opens. */
  browserSessionId: string;
  clientReportNonce: string;
  incidentType: ReportIncidentType;
  occurredAt: string;
  description?: string;
  symptomHints?: ReportSymptomHint[];
  severityHint?: ReportSeverityHint;
  attachDiagnostics?: boolean;
  attachments?: ReportAttachmentFile[];
}

export interface AssembleReportArgs {
  draft: ReportDraft;
  snapshot?: ReportItClientSnapshot;
  entities?: ReportCorrelationEntities;
  browserContext?: {
    userLocalTime: string;
    userTimeZone?: string;
    language?: string;
  };
}

/**
 * Merge a draft + captured snapshot + browser context + correlation entities
 * into the final SubmitReportInput (pure when `browserContext` is supplied;
 * otherwise reads browser globals via collectBrowserReportContext).
 */
export function assembleReportInput(args: AssembleReportArgs): SubmitReportInput {
  const ctx = args.browserContext ?? collectBrowserReportContext();

  return {
    environment: args.draft.environment,
    eventId: args.draft.eventId,
    browserSessionId: args.draft.browserSessionId,
    clientReportNonce: args.draft.clientReportNonce,
    incidentType: args.draft.incidentType,
    occurredAt: args.draft.occurredAt,
    description: args.draft.description,
    symptomHints: args.draft.symptomHints,
    severityHint: args.draft.severityHint,
    attachDiagnostics: args.draft.attachDiagnostics,
    attachments: buildAttachmentDeclarations(args.draft.attachments),
    userLocalTime: ctx.userLocalTime,
    userTimeZone: ctx.userTimeZone,
    language: ctx.language,
    entities: args.entities,
    clientSnapshot: args.snapshot,
  };
}

export interface ReportItSubmissionDeps {
  capture: (
    opts: CaptureReportItSnapshotOptions,
  ) => Promise<ReportItClientSnapshot>;
  submit: (input: SubmitReportInput) => Promise<SubmitReportResponse>;
  uploadAttachments?: (
    files: ReportAttachmentFile[] | undefined,
    uploads: SubmitReportResponse['attachmentUploads'],
  ) => Promise<void>;
  getVideoElement: () => HTMLVideoElement | null;
  getAudioElement?: () => HTMLMediaElement | null;
  getStats?: () => Promise<RTCStatsReport>;
  getEntities?: () => ReportCorrelationEntities;
  browserContext?: () => {
    userLocalTime: string;
    userTimeZone?: string;
    language?: string;
  };
}

/**
 * Run the full submission flow: capture the client snapshot (best-effort —
 * a capture failure must not block the user's report), assemble the payload,
 * and submit. Returns the API response.
 */
export async function runReportItSubmission(
  deps: ReportItSubmissionDeps,
  draft: ReportDraft,
): Promise<SubmitReportResponse> {
  let snapshot: ReportItClientSnapshot | undefined;

  try {
    snapshot = await deps.capture({
      video: deps.getVideoElement(),
      audio: deps.getAudioElement ? deps.getAudioElement() : undefined,
      getStats: deps.getStats,
    });
  } catch {
    // A diagnostic-capture failure must never stop a user from reporting — the
    // backend playbook degrades to needs_manual_review without a snapshot.
    snapshot = undefined;
  }

  const input = assembleReportInput({
    draft,
    snapshot,
    entities: deps.getEntities ? deps.getEntities() : undefined,
    browserContext: deps.browserContext ? deps.browserContext() : undefined,
  });

  const response = await deps.submit(input);
  const upload = deps.uploadAttachments ?? uploadReportAttachments;
  await upload(draft.attachments, response.attachmentUploads);

  return response;
}
